import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * MOXIE: modal "Compra rápida" (mobile). Extiende el DialogComponent nativo (assets/dialog.js) sin
 * modificarlo — showDialog()/closeDialog()/toggleDialog() se heredan tal cual. Solo agrega
 * selectSize() para reutilizar el ProductFormComponent nativo (assets/product-form.js) al agregar
 * al carrito, igual que en custom-product-card-size-select.js.
 *
 * @extends DialogComponent
 */
export class QuickViewDialog extends DialogComponent {
  connectedCallback() {
    // MOXIE: vive en body para que los taps no burbujeen a la card (cambiaban la imagen de la galería).
    if (this.parentElement !== document.body) {
      const stale = document.body.querySelector(`:scope > #${CSS.escape(this.id)}`);
      if (stale && stale !== this) stale.remove();
      document.body.append(this);
      return;
    }

    super.connectedCallback();

    this.#abortController = new AbortController();
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
  }

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #pendingRow = null;

  /** @type {number | undefined} */
  #addedTimeout;

  /**
   * @param {PointerEvent} event
   */
  selectSize(event) {
    // MOXIE: event.currentTarget is document (the delegated listener root, see
    // assets/component.js) — the clicked button only comes through as event.target.
    const target = /** @type {HTMLButtonElement | null} */ (event.target);
    if (!target || target.disabled || this.#pendingRow) return;

    const variantId = target.dataset.variantId;
    if (!variantId) return;

    const productForm = /** @type {import('./product-form').ProductFormComponent | null} */ (
      this.querySelector('product-form-component')
    );
    const variantIdInput = productForm?.refs?.variantId;
    if (!productForm || !variantIdInput) return;

    this.#setRowState(target.closest('.quick-view-modal__size-row'), 'loading');

    variantIdInput.value = variantId;
    productForm.handleSubmit(event);
  }

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartUpdate = (event) => {
    const row = this.#pendingRow;
    if (!row) return;

    event.promise
      ?.then(({ detail }) => this.#setRowState(row, detail?.didError ? null : 'added'))
      .catch(() => this.#setRowState(row, null));
  };

  /**
   * @param {Element | null} row
   * @param {'loading' | 'added' | null} state
   */
  #setRowState(row, state) {
    if (!(row instanceof HTMLElement)) return;

    clearTimeout(this.#addedTimeout);
    this.querySelectorAll('.quick-view-modal__size-row[data-state]').forEach((el) => el.removeAttribute('data-state'));
    this.#pendingRow = state === 'loading' ? row : null;
    this.refs.dialog?.toggleAttribute('aria-busy', state === 'loading');

    if (!state) return;

    row.dataset.state = state;
    // MOXIE: 'loading' se libera solo si el add nunca responde, para no bloquear la fila.
    this.#addedTimeout = setTimeout(() => this.#setRowState(row, null), state === 'added' ? 2000 : 10000);
  }

  showSizeGuide() {
    const { sizesView, guideView } = this.refs;
    if (!guideView) return;

    guideView.removeAttribute('hidden');
    sizesView?.setAttribute('hidden', '');
    guideView.querySelector('.quick-view-modal__guide-back')?.focus();
  }

  backToSizes() {
    const { sizesView, guideView } = this.refs;
    if (!guideView) return;

    sizesView?.removeAttribute('hidden');
    guideView.setAttribute('hidden', '');
    sizesView?.querySelector('.quick-view-modal__size-guide')?.focus();
  }

  /**
   * MOXIE: no recalcula nada — cada celda de la tabla ya trae ambos valores
   * (data-cm/data-in) precargados desde el metaobject size_guide, esto solo
   * decide cuál mostrar.
   * @param {PointerEvent} event
   */
  setUnit(event) {
    const target = /** @type {HTMLButtonElement | null} */ (event.target);
    const unit = target?.dataset.unit;
    if (!unit) return;

    const { unitToggle } = this.refs;
    if (!unitToggle) return;

    unitToggle.dataset.activeUnit = unit;

    for (const button of unitToggle.querySelectorAll('.quick-view-modal__guide-unit')) {
      button.setAttribute('aria-pressed', String(button.dataset.unit === unit));
    }

    for (const cell of this.querySelectorAll('.quick-view-modal__guide-table td[data-cm]')) {
      const value = unit === 'in' ? cell.dataset.in : cell.dataset.cm;
      if (value) cell.textContent = value;
    }
  }
}

if (!customElements.get('custom-quick-view-modal')) {
  customElements.define('custom-quick-view-modal', QuickViewDialog);
}
